import { AfterViewInit, Directive, forwardRef, inject, Input, OnDestroy, OnInit, Provider, Type } from '@angular/core';
import { MatSort, MatSortable, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableDataSource } from '@dice/app/directives/table-data-source';
import { TableSortUtil } from '@dice/app/directives/table-sort.util';
import CoreTableDataSource from '@dice/app/libraries/core-table-virtual-scrolling/data-source';
import { Column, TableSortComponent } from '@dice/app/types/table.types';
import { has as _has } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

export function provideSortComponent(component: Type<unknown>): Provider {
  return { provide: TableSortComponent, useExisting: forwardRef(() => component) };
}

@Directive({
  selector: 'mat-table[tableSort]',
  standalone: false,
})
export class TableSortDirective implements OnInit, AfterViewInit, OnDestroy {
  private tableSortComponent = inject<TableSortComponent<unknown>>(TableSortComponent);
  private matSort = inject(MatSort, { host: true });

  @Input() dataSource: MatTableDataSource<unknown> | CoreTableDataSource<unknown> | TableDataSource<unknown>;

  private defaultSortHeader: MatSortHeader;

  private matSortHeaders: MatSortHeader[] = [];

  private hasDefaultSortHeadersDefinition: boolean = false;

  private hasColumnsDefinition: boolean = false;

  private readonly DEFAULT_SORT_DIRECTION = 'asc';

  private readonly unsubscriber$: Subject<void> = new Subject<void>();

  private disableSort: boolean = false;

  private tableSortUtil: TableSortUtil;

  ngOnInit(): void {
    this.readTableSortComponentValues();
  }

  ngAfterViewInit(): void {
    if (!(this.dataSource instanceof TableDataSource)) {
      this.tableSortUtil = new TableSortUtil();
      this.dataSource.sortData = this.tableSortUtil.sortData.bind(this.tableSortUtil);
    }
    this.dataSource.sort = this.matSort;
    const { sortables } = this.dataSource.sort;
    // Incorrect Angular Material documentation/definition file in 'MatSort' for 'sortables'.
    this.matSortHeaders = Array.from(sortables.values()) as MatSortHeader[];
    if (this.disableSort) {
      this.disableSortForAllHeaders();
    } else {
      this.dataSource.sort.sortChange.pipe(takeUntil(this.unsubscriber$), tap(this.sortChange.bind(this))).subscribe();
      this.initSortHeaders();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next();
  }

  private readTableSortComponentValues(): void {
    this.disableSort = this.tableSortComponent.disableSort;
    this.hasDefaultSortHeadersDefinition =
      _has(this, 'tableSortComponent.defaultSorting.columnName') &&
      _has(this, 'tableSortComponent.defaultSorting.direction');
    this.hasColumnsDefinition = _has(this, 'tableSortComponent.columns') && this.tableSortComponent.columns.length > 0;
  }

  private sortChange(matSort: Sort): void {
    const { active, direction } = matSort;
    if (!active || !direction) {
      this.sortTableByDefaultDefinition();
    }
  }

  private disableSortForAllHeaders(): void {
    this.matSortHeaders.forEach((header: MatSortHeader) => {
      header.disabled = true;
    });
  }

  private initSortHeaders(): void {
    Promise.resolve().then(() => {
      this.setSortHeaderDisabledOption();
      this.getDefaultSortHeader();
      this.setSortHeaderDefaultOptions();
      this.sortTableByDefaultDefinition();
    });
  }

  private getColumnForMatSortHeader(header: MatSortHeader): Column {
    return !this.hasColumnsDefinition
      ? null
      : this.tableSortComponent.columns.find((col: Column) => {
          const { table, name } = col;
          const { id } = header;
          return ('displayValue' in table && table.displayValue === id) || name === id;
        });
  }

  private setSortHeaderDisabledOption(): void {
    this.matSortHeaders.forEach((header: MatSortHeader) => {
      const column: Column = this.getColumnForMatSortHeader(header);
      header.disabled = header.disabled || this.isSortDisabledForColumn(column);
    });
  }

  private setSortHeaderDefaultOptions(): void {
    this.matSortHeaders.forEach((header: MatSortHeader) => {
      const isDefaultSortHeader = header === this.defaultSortHeader;
      header.start =
        isDefaultSortHeader && this.hasDefaultSortHeadersDefinition
          ? this.tableSortComponent.defaultSorting.direction
          : this.DEFAULT_SORT_DIRECTION;
      header.disableClear = isDefaultSortHeader;
    });
  }

  /**
   * Tries to get the SortHeader as defined per TableSortComponent::defaultSorting.
   * If unsuccessful or not defined uses the first sortable header as DefaultSortHeader.
   */
  private getDefaultSortHeader(): void {
    if (this.hasDefaultSortHeadersDefinition) {
      const {
        defaultSorting: { columnName },
        columns,
      } = this.tableSortComponent;

      const matSortHeader: MatSortHeader = this.matSortHeaders.find((header: MatSortHeader) => {
        let found: boolean = false;

        if (this.hasColumnsDefinition) {
          const defaultSortColumn: Column = columns.find((findColumn: Column) => {
            return findColumn.name === columnName;
          });
          found =
            defaultSortColumn &&
            defaultSortColumn.table &&
            'displayValue' in defaultSortColumn.table &&
            defaultSortColumn.table.displayValue === header.id;
        }

        return found || columnName === header.id;
      });

      if (!matSortHeader || matSortHeader.disabled) {
        customLogger.error('[Table Component]: Defined default sort column does not exist or is disabled for sorting.');
      } else {
        this.defaultSortHeader = matSortHeader;
      }
    }
    if (!this.defaultSortHeader) {
      const firstSortableHeaderIndex: number = this.matSortHeaders.findIndex((value: MatSortHeader) => !value.disabled);
      if (firstSortableHeaderIndex > -1) {
        this.defaultSortHeader = this.matSortHeaders[firstSortableHeaderIndex];
      }
    }
  }

  private sortTableByDefaultDefinition(): void {
    if (this.defaultSortHeader) {
      const { id, start } = this.defaultSortHeader;
      this.dataSource.sort.sort({
        id,
        start,
        disableClear: true,
      } as MatSortable);
    }
  }

  private isSortDisabledForColumn(column: Column): boolean {
    if (column == null) {
      return false;
    }
    return !column.table.type.sortEnabled;
  }
}
